/**
 * GlAccountInlineSelect - canonical inline GL/category picker for the bank feed
 * (table + pending/completed cards).
 *
 * Jul 16 overhaul (Phases 1+2):
 *  • No typing → whole COA grouped by FS category, likely nature on top.
 *  • Typing → "Top suggestions" (max 3) ranked by word-aware text relevance
 *    + accounting semantics for the money direction (inflow+"loan" boosts
 *    loan LIABILITIES; outflow+"loan" boosts receivables) + how often this
 *    business actually uses each account. Below it, "All matches" ordered
 *    by relevance. cmdk's fuzzy-subsequence noise is disabled entirely.
 *  • Every option labelled with its category/subcategory badge.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Button } from '../ui/button';
import { ChevronsUpDown, Check, Loader2, Sparkles, Plus, Lightbulb } from 'lucide-react';
import { useGlConcepts } from '../../utils/glSemantic';
import { groupAccountsByType, inferPreferredTypes, rankGlAccounts, glTypeMeta, prettySubType } from '../common/glAccountMeta';
import { getGlUsageStats } from '../../utils/glUsageStats';
import NewAccountModal from '../accounting/NewAccountModal';
import { clearGLAccountsCache } from '../common/GLAccountSelector';

const AccountRow = ({ a, selected, onSelect, highlight = false, testId, meaning = null }) => {
  const meta = glTypeMeta(a.type);
  const sub = prettySubType(a.sub_type);
  return (
    <CommandItem
      key={a.code}
      value={`${highlight ? 'top::' : ''}${a.code}::${a.name}`}
      onSelect={() => onSelect(a)}
      className={`group flex items-center gap-2 text-xs cursor-pointer text-foreground data-[selected=true]:bg-[#108981] data-[selected=true]:text-white ${highlight ? 'font-semibold border-l-2 border-[#108981] bg-[#108981]/10' : ''}`}
      data-testid={testId}
    >
      <Check className={`h-3 w-3 shrink-0 ${selected ? 'opacity-100 text-[#108981] group-data-[selected=true]:text-white' : 'opacity-0'}`} />
      <span className="font-mono text-slate-600 group-data-[selected=true]:text-white/90 w-11 shrink-0">{a.code}</span>
      <span className="truncate flex-1">{a.name}</span>
      {meaning && (
        <span className="shrink-0 inline-flex items-center gap-0.5 px-1 py-px rounded border border-amber-300 bg-amber-50 text-amber-800 text-[9px] font-medium group-data-[selected=true]:bg-white/95" title={`“${meaning.phrase}” usually means ${meaning.concept}`} data-testid="gl-by-meaning">
          <Lightbulb className="w-2.5 h-2.5" />By meaning
        </span>
      )}
      <span className={`shrink-0 px-1 py-px rounded border text-[9px] font-medium group-data-[selected=true]:bg-white/95 ${meta.badge}`}>
        {sub || meta.label}
      </span>
    </CommandItem>
  );
};

export const GlAccountInlineSelect = ({
  value,
  valueName,
  accounts = [],
  onChange,
  saving = false,
  direction = null,      // 'inflow' | 'outflow': drives semantic ranking
  edited = false,        // true when the row has a staged (unsaved) edit
  testId,
  allowCreate = true,    // iter1101 - show "+ Create new account" footer that
                         // opens the canonical NewAccountModal (same one used
                         // in Chart of Accounts, invoices, bills, etc.).
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [usage, setUsage] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const concepts = useGlConcepts();

  useEffect(() => {
    if (open && usage === null) {
      getGlUsageStats().then(setUsage).catch(() => setUsage({}));
    }
  }, [open, usage]);

  const current = useMemo(() => accounts.find((a) => a.code === value), [accounts, value]);
  const currentMeta = glTypeMeta(current?.type);

  const display = useMemo(() => {
    if (saving) return 'Saving…';
    if (valueName) return valueName;
    return current ? current.name : (value || 'Choose a category…');
  }, [valueName, current, value, saving]);

  const preferredTypes = useMemo(
    () => inferPreferredTypes({ currentCode: value, accounts, direction }),
    [value, accounts, direction],
  );

  const browseGroups = useMemo(
    () => groupAccountsByType(accounts, preferredTypes),
    [accounts, preferredTypes],
  );

  const ranked = useMemo(() => {
    if (!query.trim()) return null;
    return rankGlAccounts({ query, accounts, direction, usage, topN: 3, concepts });
  }, [query, accounts, direction, usage, concepts]);

  const handleSelect = (a) => {
    setOpen(false);
    setQuery('');
    if (a.code !== value) onChange?.(a);
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) setQuery('');
  };

  return (
    <>
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={saving}
          className={`group inline-flex items-center gap-1.5 h-8 pl-2 pr-1.5 rounded-lg border text-xs font-medium text-left
            min-w-[170px] max-w-[280px] transition-all shadow-sm
            ${edited
              ? 'border-amber-400 bg-amber-50/70 hover:border-amber-500'
              : 'border-slate-300 bg-white dark:bg-slate-900 dark:border-slate-700 hover:border-[#108981] hover:ring-2 hover:ring-[#108981]/15'}
            ${saving ? 'opacity-70 cursor-wait' : 'cursor-pointer'}`}
          title="Click to change the category (GL account)"
          data-testid={testId || `gl-inline-${value}`}
        >
          {saving
            ? <Loader2 className="w-3 h-3 animate-spin shrink-0 text-muted-foreground" />
            : <span className={`w-2 h-2 rounded-full shrink-0 ${edited ? 'bg-amber-500' : currentMeta.dot}`} />}
          <span className="truncate flex-1 text-foreground">{display}</span>
          {current && !saving && (
            <span className={`shrink-0 px-1 py-px rounded border text-[9px] font-semibold uppercase tracking-wide ${currentMeta.badge}`}>
              {currentMeta.label}
            </span>
          )}
          <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-40 group-hover:opacity-80" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[360px]" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search all accounts (e.g. Rent, Loan, Equipment)…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList className="max-h-[320px]">
            <CommandEmpty>No matches across your chart of accounts</CommandEmpty>
            {current && !query.trim() && (
              <CommandGroup
                heading={(
                  <span className="flex items-center gap-1.5">
                    <Check className="w-3 h-3 text-[#108981]" />
                    <span className="text-[#108981] font-semibold">Currently selected</span>
                  </span>
                )}
              >
                <AccountRow
                  a={current}
                  selected
                  onSelect={handleSelect}
                  highlight
                  testId={testId ? `${testId}-current` : undefined}
                />
              </CommandGroup>
            )}
            {ranked ? (
              <>
                {ranked.top.length > 0 && (
                  <CommandGroup
                    heading={(
                      <span className="flex items-center gap-1.5">
                        <Sparkles className="w-3 h-3 text-[#108981]" />
                        <span className="text-[#108981] font-semibold">Top suggestions</span>
                        {direction && (
                          <span className="text-[9px] text-muted-foreground font-normal normal-case">
                            for this {direction === 'inflow' ? 'money-in' : 'money-out'} transaction
                          </span>
                        )}
                      </span>
                    )}
                  >
                    {ranked.top.map((a) => (
                      <AccountRow
                        key={`top-${a.code}`}
                        a={a}
                        selected={a.code === value}
                        onSelect={handleSelect}
                        highlight
                        meaning={ranked.meaning?.get(a.code)}
                        testId={testId ? `${testId}-top-${a.code}` : undefined}
                      />
                    ))}
                  </CommandGroup>
                )}
                {ranked.groups.map(({ type, meta, accounts: list }) => (
                  <CommandGroup key={type} heading={`All matches · ${meta.plural}`}>
                    {list.map((a) => (
                      <AccountRow
                        key={a.code}
                        a={a}
                        selected={a.code === value}
                        onSelect={handleSelect}
                        meaning={ranked.meaning?.get(a.code)}
                        testId={testId ? `${testId}-opt-${a.code}` : undefined}
                      />
                    ))}
                  </CommandGroup>
                ))}
              </>
            ) : (
              browseGroups.map(({ type, meta, accounts: list, isPreferred }) => (
                <CommandGroup
                  key={type}
                  heading={(
                    <span className="flex items-center gap-1.5">
                      {isPreferred && <Sparkles className="w-3 h-3 text-[#108981]" />}
                      <span className={isPreferred ? 'text-[#108981] font-semibold' : ''}>
                        {isPreferred ? `Likely matches · ${meta.plural}` : meta.plural}
                      </span>
                    </span>
                  )}
                >
                  {list.map((a) => (
                    <AccountRow
                      key={a.code}
                      a={a}
                      selected={a.code === value}
                      onSelect={handleSelect}
                      testId={testId ? `${testId}-opt-${a.code}` : undefined}
                    />
                  ))}
                </CommandGroup>
              ))
            )}
          </CommandList>
          {allowCreate && (
            <div className="border-t bg-muted/30 p-1">
              <Button
                type="button"
                variant="ghost"
                className="w-full justify-start text-[#108981] hover:bg-[#108981]/10 hover:text-[#108981] h-8 text-xs"
                onClick={() => { setOpen(false); setCreateOpen(true); }}
                data-testid={testId ? `${testId}-create-new` : 'gl-inline-create-new'}
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Create new account…
              </Button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
    {allowCreate && (
      <NewAccountModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        existingAccounts={accounts}
        defaultType={direction === 'inflow' ? 'revenue' : 'expense'}
        onAccountCreated={(created) => {
          // Bust any shared GL-account caches so other pickers on the page
          // (invoices, bills, COA) refresh too.
          try { clearGLAccountsCache(); } catch (_) { /* non-fatal */ }
          // Broadcast to the useBankFeedData hook so `availableGlAccounts`
          // gets refetched and the newly-created row shows up in every
          // inline selector on the page.
          try {
            window.dispatchEvent(new CustomEvent('akountfly:gl-accounts-updated', { detail: created }));
          } catch (_) { /* non-fatal */ }
          // Auto-select the new account for THIS row so the user doesn't
          // have to reopen the picker and hunt for it.
          const acc = created?.account || created;
          if (acc && acc.code) onChange?.(acc);
        }}
      />
    )}
    </>
  );
};

export default GlAccountInlineSelect;
